import React, { useState, useContext, useEffect, useCallback } from 'react';
import style from './UpgradeCard.module.css';
import axios from 'axios';
import { UserContext } from '../../contexts/UserContext';

import { useAuthUser } from '../../hooks/useAuthUser';
import SecondaryButton from '../SecondaryButton/SecondaryButton';

const UpgradeCard = (props) => {
	useAuthUser();
	const [lemonSqueezyResponse, setLemonSqueezyResponse] = useState({});
	const [checkoutError, setCheckoutError] = useState(null);
	const [checkoutRequestId, setCheckoutRequestId] = useState(null);
	const { dbUser, isDbUserResolved } = useContext(UserContext);
	const currentPlan = dbUser?.userPlan || '';
	const isCurrentPlan = currentPlan === props.planType;
	const ctaLabel = `Buy ${props.planName} for ${props.amountText}`;
	const isNativeMobileApp = typeof window !== 'undefined' && window.__CALCIUM_NATIVE_MOBILE === true;

	const API_URL = process.env.REACT_APP_API_URL;

	const getCheckoutInfo = useCallback(async () => {
		try {
			setCheckoutError(null);
			if (!API_URL) {
				throw new Error('REACT_APP_API_URL is missing. Cannot create checkout.');
			}

			const response = await axios.post(`${API_URL}/payments/create-checkout`, {
				payload: props.lemonSqueezyData
			}, {
				timeout: 15000
			});

			const checkoutUrl = response?.data?.checkoutUrl || response?.data?.checkout?.attributes?.url || null;
			setCheckoutRequestId(response?.data?.requestId || null);
			if (!checkoutUrl) {
				throw new Error(`Checkout URL missing from API response. requestId=${response?.data?.requestId || 'unknown'}`);
			}

			setLemonSqueezyResponse({
				data: {
					data: {
						attributes: {
							url: checkoutUrl
						}
					}
				}
			});
			return checkoutUrl;
		} catch (error) {
			const axiosMeta = axios.isAxiosError(error) ? {
				message: error.message,
				code: error.code,
				status: error.response?.status,
				responseData: error.response?.data,
				requestUrl: error.config?.url,
				requestMethod: error.config?.method
			} : null;

			console.error('Error creating checkout:', {
				error: error.message,
				isAxiosError: !!axiosMeta,
				axiosMeta,
				requestId: error?.response?.data?.requestId || null,
				apiUrl: API_URL,
				planType: props.planType,
				payloadShape: {
					hasData: !!props.lemonSqueezyData?.data,
					variantId: props.lemonSqueezyData?.data?.relationships?.variant?.data?.id,
					storeId: props.lemonSqueezyData?.data?.relationships?.store?.data?.id
				}
			});

			setCheckoutError(axiosMeta?.responseData?.error || error.message || 'Failed to initialize checkout');
			return null;
		}
	}, [API_URL, props.lemonSqueezyData, props.planType]);

	const handleNativeMobilePurchase = () => {
		const queryParams = new URLSearchParams(window.location.search);
		const payload = {
			type: 'NATIVE_PAYMENT',
			planType: props.planType,
			userId: dbUser?._id || '',
		};
		if (queryParams.get('source') === 'onboarding' && queryParams.get('routineId')) {
			payload.routineId = queryParams.get('routineId');
		}
		if (window.ReactNativeWebView?.postMessage) {
			window.ReactNativeWebView.postMessage(JSON.stringify(payload));
		}
	};

	useEffect(() => {
		const initializeCheckout = async () => {
			try {
				if (!isNativeMobileApp) {
					await getCheckoutInfo();
				}
			} catch (error) {
				console.error('Failed to initialize checkout:', error);
			}
		};

		initializeCheckout();
	}, [isNativeMobileApp, getCheckoutInfo]);

	return (
		<div className={`${style.UpgradeCardWrapper} ${props.recommended ? style.Recommended : ''}`}>
			{props.recommended && <span className={style.RecommendedBadge}>Recommended</span>}
			<div className={style.UpgradeCardDetails}>
				<span className={style.UpgradeCardName}>
					{props.planName}
				</span>
				<span className={style.UpgradeCardAmount}>
					{props.amountText}
				</span>
				<span className={style.PlanDescription}>{props.text}</span>
				<span className={style.TrialText}>Free 3-day trial</span>
			</div>
			<div className={style.UpgradeCardFeaturesContainer}>
				<span className={style.UpgradeCardFeature}>
					Access to Cali AI chat bot (on release)
				</span>
				<span className={style.UpgradeCardFeature}>
					Access meal plans
				</span>
				<span className={style.UpgradeCardFeature}>
					Create routines
				</span>
				<span className={style.UpgradeCardFeature}>
					View routines
				</span>
				<span className={style.UpgradeCardFeature}>
					Get full access to Calcium
				</span>
				<span className={style.UpgradeCardFeature}>
					Earn XP
				</span>
				<span className={style.UpgradeCardFeature}>
					Increase ranks as you workout
				</span>
			</div>

		{isCurrentPlan ? (
				<SecondaryButton
					text='Current plan'
					click={() => null}
					width='100%'
				/>
			) : !isDbUserResolved ? (
				<span className={style.StatusMessage}>Checking your current plan...</span>
			) :
			isNativeMobileApp ? (
				<button
					className={style.PurchaseButton}
					onClick={handleNativeMobilePurchase}
				>
					{ctaLabel}
				</button>
			) :
			lemonSqueezyResponse?.data?.data?.attributes?.url ? (
				<button
					className={style.PurchaseButton}
					onClick={() => {
						const checkoutUrl = lemonSqueezyResponse.data.data.attributes.url;
						if (window.LemonSqueezy?.Url?.Open) {
							window.LemonSqueezy.Url.Open(checkoutUrl);
						} else {
							window.open(checkoutUrl, '_blank', 'noopener,noreferrer');
						}
					}}
				>
					{ctaLabel}
				</button>
			) : checkoutError ? (
				<span className={style.StatusMessage}>
					Payment unavailable right now. Check console logs and retry.
					{checkoutRequestId ? ` (request: ${checkoutRequestId})` : ''}
				</span>
			) : (
				<span className={style.StatusMessage}>Loading payment options...</span>
			)}
		</div>
	);

}


export default UpgradeCard;
